// Wire DTOs (mirror backend/src/types/dto.ts). The two frontends share nothing at build time.
export type Role = "USER" | "ADMIN";
export type DriverKind = "MEDIAMTX" | "SRS" | "CLOUDFLARE";
export type DriverType = "mediamtx" | "srs" | "cloudflare"; // lowercased wire form
export type StreamStatus = "scheduled" | "live" | "waiting" | "paused" | "offline" | "ended";
export type AuthorTypeWire = "user" | "anon" | "system";

export type PlaybackProtocol = "whep" | "llhls" | "hls" | "flv";
export type PlaybackTier = "sub-second" | "low-latency" | "standard";

export interface PlaybackEndpoint {
  protocol: PlaybackProtocol;
  tier: PlaybackTier;
  url: string;
  priority: number;
  sameRegionOnly?: boolean;
  latencyHint?: number;
}

export interface IngestEndpoint {
  nodeId: string;
  nodeName: string;
  region: string;
  driver: DriverKind;
  protocol: "srt" | "rtmp" | "rtmps";
  url: string;
  streamId?: string;
  rtmpStreamKeyField?: string;
  obsHint: string;
  srtPassphraseRequired?: boolean;
}

export interface Capabilities {
  isAdmin: boolean;
  canComment: boolean;
  canPublish: boolean | { nodes: string[]; drivers: string[] };
  canRecord: boolean;
}

export interface MeDTO {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  role: Role;
  capabilities: Capabilities;
}

export interface RecordingDTO {
  id: string;
  status: "processing" | "ready" | "failed";
  driver: DriverKind;
  url: string | null;
  durationSec: number | null;
  sizeBytes: number | null;
  createdAt: string;
  readyAt: string | null;
  expiresAt: string | null;
}

export interface StreamDetailDTO {
  id: string;
  uuid: string;
  slug: string | null;
  title: string;
  status: StreamStatus;
  scheduledStartAt: string | null;
  featured: boolean;
  recordEnabled: boolean;
  isOwner: boolean;
  author: { id: string; name: string; avatar: string | null };
  liveNode?: { id: string; name: string; region: string; driver: DriverType } | null;
  playbackEndpoints: PlaybackEndpoint[];
  recordings?: RecordingDTO[];
  viewerCount: number;
  startedAt: string | null;
  description?: string;
}

export interface StreamCardDTO {
  uuid: string;
  slug: string | null;
  title: string;
  status: StreamStatus;
  scheduledStartAt: string | null;
  author: { name: string; avatar: string | null };
  viewerCount: number;
  region: string | null;
}

// Owner-facing stream summary for the Creator Studio (GET /api/me/streams).
export interface MyStreamDTO {
  uuid: string;
  slug: string | null;
  title: string;
  status: StreamStatus;
  featured: boolean;
  recordEnabled: boolean;
  viewerCount: number;
  scheduledStartAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  region: string | null;
  recordingCount: number;
  latestRecordingStatus: "processing" | "ready" | "failed" | null;
}

export interface StreamWithSecretsDTO {
  id: string;
  uuid: string;
  slug: string | null;
  title: string;
  streamKey: string;
  scheduledStartAt: string | null;
  recordEnabled: boolean;
  ingestEndpoints: IngestEndpoint[];
}

// Owner-facing push/ingest info for an existing stream (GET /api/streams/:uuid/ingest).
// keyHidden=true means the stream-key field is a placeholder (self-hosted; our key is hashed).
export interface IngestInfoDTO {
  ingestEndpoints: IngestEndpoint[];
  keyHidden: boolean;
}

export interface CommentDTO {
  id: string;
  type: "comment";
  content: string;
  ts: number;
  authorType: AuthorTypeWire;
  author: { name: string; avatar: string | null; isStreamer: boolean };
}

export interface IdentitySummary {
  id: string;
  kind: "identity" | "passkey";
  provider?: string;
  label: string;
  createdAt: string;
}

/** Helper: does the user have any publish capability (drives "Go Live" visibility). */
export function hasPublish(cap: Capabilities | undefined): boolean {
  if (!cap) return false;
  if (cap.canPublish === true) return true;
  if (typeof cap.canPublish === "object")
    return cap.canPublish.nodes.length > 0 || cap.canPublish.drivers.length > 0;
  return false;
}
